import {
  ClockCircleOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  OrderedListOutlined,
  ReloadOutlined,
  RightOutlined,
  TrophyOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons"
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Form,
  Layout,
  Modal,
  message,
  Row,
  Space,
  Spin,
  Statistic,
  Tag,
  Typography,
} from "antd"
import Countdown from "antd/lib/statistic/Countdown"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "react-query"
import { Link, useParams } from "react-router-dom"
import { AssessmentFailed } from "../../../components/AssessmentFailed"
import { AssessmentLimit } from "../../../components/AssessmentLimit"
import { BoldText } from "../../../components/BoldText"
import { FreeText } from "../../../components/questions/FreeText"
import { MatchColumns } from "../../../components/questions/MatchColumns"
import { MultipleChoice } from "../../../components/questions/MultipleChoice"
import { OrderCorrectly } from "../../../components/questions/OrderCorrectly"
import { ScaleRating } from "../../../components/questions/ScaleRating"
import { TrueAndFalse } from "../../../components/questions/TrueAndFalse"
import { trackAction, useLesson } from "../../../hooks/lesson-context"
import { useQueryParam } from "../../../hooks/useQueryParam"
import { useActivity } from "../../../network/useEnrolment"
import { useEnrolmentActivtyMutation } from "../../../network/useEnrolmentActivity"
import { useGetAssessment, useSubmitAssessment } from "../../../network/useLearning"
import { useGetPerson } from "../../../network/usePerson"

const { Footer } = Layout

const defaultErrorMessage = "Please answer question"

// Helper function to check if a string contains HTML tags
function containsHTML(str) {
  if (!str || typeof str !== "string") return false
  // More specific regex to detect actual HTML tags, not just < and > characters
  const htmlRegex = /<[a-zA-Z][^>]*>/g
  return htmlRegex.test(str)
}

export function Assessments({ lessonQuery, enrolmentQuery, moduleQuery }) {
  const {
    data: { current: assessment },
  } = lessonQuery

  const [assessmentAttemptModal, setAssessmentAttemptModal] = useState(false)
  const [assessmentAttemptModalFromSubmission, setAssessmentAttemptModalFromSubmission] = useState(false)
  const [assessmentLimitModal, setAssessmentLimitModal] = useState(false)
  const [assessmentInfoModalVisible, setAssessmentInfoModalVisible] = useState(false)
  const [savedSubmission, setSavedSubmission] = useState(null)
  const { state: ctxState, dispatch: ctxDispatch } = useLesson()
  const { enrolmentUid, lessonUid, enrolmentType } = useParams()

  const assessmentQuery = useGetAssessment({
    assessmentUid: lessonUid,
    enrolmentUid,
    filters: {},
  })

  const activityQuery = useActivity({ uid: enrolmentUid })
  const queryClient = useQueryClient()
  const queryParam = useQueryParam()
  const isAdmin = queryParam.get("isAdmin")
  const isFacilitate = queryParam.get("facilitate")
  const { t } = useTranslation()

  const { mutateAsync: submitAssessment, isLoading: isSubmitting } = useSubmitAssessment({
    enrolmentUid,
    assessmentUid: lessonUid,
  })

  useEffect(() => {
    if (!assessmentQuery.isLoading && assessmentQuery.data) {
      if (
        assessmentQuery?.data?.attemptsUsed < assessmentQuery?.data?.currentAttempt &&
        assessmentQuery?.data?.attemptsUsed > 0 &&
        assessmentQuery?.data?.passed === false &&
        !assessmentAttemptModalFromSubmission
      ) {
        setAssessmentAttemptModal(true)
      }
    }

    if (!assessmentQuery?.isLoading && !assessmentQuery?.data) {
      if (
        assessmentQuery?.error?.response?.data?.message ===
        "All attempts have been used up, please request more through an admin"
      ) {
        setAssessmentLimitModal(true)
      }
    }

    if (!assessmentQuery?.isLoading && assessmentQuery?.data?.passed && assessmentQuery?.data) {
    }
  }, [
    assessmentQuery?.data,
    assessmentQuery?.isLoading,
    assessmentQuery?.error,
    assessmentQuery?.data?.attemptsUsed,
    assessmentQuery?.data?.currentAttempt,
  ])

  useEffect(() => {
    if (!assessmentQuery?.isLoading && assessmentQuery?.data?.passed && assessment && !assessment?.revealResult) {
      ctxDispatch({
        type: "next",
        payload: {
          nextUrlByPathname,
          queryClient,
          lessonUid,
          enrolmentUid,
          enrolmentType,
          currentModule: moduleQuery?.data,
          ...lessonQuery?.data?.next,
        },
      })
    }
  }, [assessmentQuery?.data, assessmentQuery?.isLoading, assessment])

  const onCountdownFinished = () => {
    if (!assessmentQuery?.data?.isPreviewing) {
      if (process.env.REACT_APP_ENV === "production") {
        submitAssessment()
      } else {
        alert(t("assessmentTimeLimitReacher"))
      }
    }
  }

  const [form] = Form.useForm()
  const personQuery = useGetPerson({ authenticated: true })
  const mutator = useEnrolmentActivtyMutation()

  const nextUrlByPathname = useMemo(() => {
    if (
      enrolmentQuery?.data &&
      !enrolmentQuery?.isLoading &&
      moduleQuery?.data &&
      !moduleQuery?.isLoading &&
      lessonQuery?.data &&
      !lessonQuery?.isLoading
    ) {
      if (!lessonQuery?.data?.next?.lessonUid || !lessonQuery?.data?.next?.moduleUid) return false

      const nextModuleIsTheSameAsCurrentModule = moduleQuery?.data?.uid === lessonQuery?.data?.next?.moduleUid

      if (nextModuleIsTheSameAsCurrentModule) return true

      if (enrolmentType.includes("course")) {
        const nextModule = enrolmentQuery?.data?.publication?.material?.find(
          (x) => x.uid === lessonQuery?.data?.next?.moduleUid
        )

        return nextModule?.learning?.find((x) => x.uid === lessonQuery?.data?.next?.lessonUid)?.kind === "assessment"
      } else {
        let nextModule

        enrolmentQuery?.data?.publication?.material?.forEach((section) => {
          section?.modules?.forEach((mod) => {
            if (mod.uid === lessonQuery?.data?.next?.moduleUid) {
              nextModule = mod
            }
          })
        })

        return nextModule?.learning?.find((x) => x.uid === lessonQuery?.data?.next?.lessonUid)?.kind === "assessment"
      }
    }
    return false
  }, [
    lessonQuery?.data,
    moduleQuery?.date,
    moduleQuery?.isLoading,
    !lessonQuery?.isLoading,
    enrolmentQuery?.data,
    !enrolmentQuery?.isLoading,
  ])

  const assessmentInfoStorageKey = useMemo(() => {
    if (!enrolmentUid || !lessonUid) return null
    return `assessment_info_seen:${enrolmentUid}:${lessonUid}`
  }, [enrolmentUid, lessonUid])

  const assessmentInfo = useMemo(() => {
    const liveData = assessmentQuery?.data || {}
    const lessonData = assessment || {}

    const attempts = Number(liveData?.allowedAttempts ?? lessonData?.allowedAttempts ?? 0)
    const timeLimitMinutes =
      Number(liveData?.timeLimit ?? lessonData?.timeLimit ?? lessonData?.duration?.minutes ?? 0) || null
    const passScore = Number(liveData?.passScore ?? lessonData?.passScore ?? 0)
    const passScoreRequired = Boolean(liveData?.passScoreRequired ?? lessonData?.passScoreRequired)
    const repeatFailedQuestions = Boolean(liveData?.repeatFailedQuestions ?? lessonData?.repeatFailedQuestions)
    const completeQuestionsInOrder = Boolean(
      liveData?.completeQuestionsInOrder ?? lessonData?.questionsRequirePreviousAnswer
    )
    const revealResult = Boolean(liveData?.revealResult ?? lessonData?.revealResult)
    const questionCount = Number(liveData?.questions?.length ?? lessonData?.questions?.length ?? 0)

    return {
      attempts,
      timeLimitMinutes,
      passScore,
      passScoreRequired,
      repeatFailedQuestions,
      completeQuestionsInOrder,
      revealResult,
      questionCount,
    }
  }, [assessmentQuery?.data, assessment])

  useEffect(() => {
    if (assessmentQuery?.isLoading || !assessmentQuery?.data || !assessmentInfoStorageKey) return
    if (assessmentQuery?.data?.isPreviewing) return

    const attemptsUsed = Number(assessmentQuery?.data?.attemptsUsed ?? 0)
    const currentAttempt = Number(assessmentQuery?.data?.currentAttempt ?? 0)
    const isFirstLiveAttempt = attemptsUsed === 0 && currentAttempt === 1

    if (!isFirstLiveAttempt) return

    const hasSeenAssessmentInfo = window.localStorage.getItem(assessmentInfoStorageKey) === "1"
    if (hasSeenAssessmentInfo) return

    setAssessmentInfoModalVisible(true)
    window.localStorage.setItem(assessmentInfoStorageKey, "1")
  }, [
    assessmentQuery?.isLoading,
    assessmentQuery?.data,
    assessmentQuery?.data?.isPreviewing,
    assessmentQuery?.data?.attemptsUsed,
    assessmentQuery?.data?.currentAttempt,
    assessmentInfoStorageKey,
  ])

  if (
    assessmentQuery?.isLoading ||
    assessmentQuery?.isFetching ||
    enrolmentQuery?.isLoading ||
    enrolmentQuery?.isFetching
  ) {
    return (
      <div
        className="loading__spin"
        style={{
          position: "absolute",
          zIndex: "100",
          top: 0,
          right: 0,
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(6px) brightness(90%)",
        }}
      >
        <Spin tip={t("settingUpAssessment")} />
      </div>
    )
  }

  async function trackQuestionSubmission({ data, typeUid, type }) {
    message.loading("Saving progress...", 1)
    try {
      await trackAction({
        payload: {
          typeUid,
          type: "question",
          status: "completed",
          context: {
            attempt: assessmentQuery?.data?.currentAttempt,
            type,
            submission: {
              key: typeUid,
              data: data,
            },
          },
        },
        mutator,
        publicationEnrolmentUid: enrolmentUid,
        dispatch: ctxDispatch,
      })

      const indexOfQuestion = assessmentQuery?.data?.questions?.findIndex((q) => q.uid === typeUid)

      if (indexOfQuestion < 0) return
      const nextQuestion = assessmentQuery?.data?.questions[indexOfQuestion + 1]

      if (!nextQuestion) return

      const nextQuestionElement = document.getElementById(nextQuestion.uid)

      if (nextQuestionElement) {
        nextQuestionElement.scrollIntoView({ behavior: "smooth" })
      }
    } catch (error) {
      form.resetFields([typeUid])
      // message.error(error.response.data.message || "Failed to save question progress")
    }
  }
  const initialValues = assessmentQuery?.data?.questions?.reduce((acc, q) => {
    if (q?.type === "free-text") {
      return {
        ...acc,
        [q?.uid]:
          q?.response ??
          q?.submission?.data ??
          q?.answers?.find((a) => a?.isSelected)?.content?.title ??
          q?.answers?.find((a) => a?.isSelected)?.value ??
          undefined,
      }
    }

    return {
      ...acc,
      [q?.uid]: q?.answers?.filter((a) => a?.isSelected)?.length
        ? q?.type === "true-false"
          ? q?.answers?.some((a) => a?.isSelected)
            ? q?.answers?.find((a) => a?.isSelected)?.correct === 1
              ? 1
              : 0
            : undefined
          : q?.answers
              ?.filter((a) => a?.isSelected)
              ?.sort((a, b) => a?.selectedIndex - b?.selectedIndex) // This will sort from lowest to highest (0 to 10)
              ?.map((a) =>
                q?.type === "multiple-choice" || q?.type === "scale-rating" ? JSON.stringify(a) : a
              )
        : undefined,
    }
  }, {})

  if (assessmentQuery?.data?.passed && assessment && !assessment?.revealResult) return null

  return (
    <Layout style={{ background: "#f8f8f881" }}>
      <AssessmentFailed
        person={personQuery?.data}
        visible={assessmentAttemptModal}
        onClose={async () => {
          if (assessmentAttemptModalFromSubmission) {
            await queryClient.invalidateQueries()
          }
          form.resetFields()
          setAssessmentAttemptModal(false)
        }}
        assessment={{
          ...assessmentQuery?.data,
          ...(savedSubmission ? { ...savedSubmission } : {}),
        }}
        form={form}
      />
      <AssessmentLimit
        person={personQuery?.data}
        visible={assessmentLimitModal}
        onClose={() => setAssessmentLimitModal(false)}
        assessment={assessmentQuery?.data}
      />
      <Modal
        className="modal modal--assessmentInfo"
        title={null}
        visible={assessmentInfoModalVisible}
        footer={null}
        onCancel={() => setAssessmentInfoModalVisible(false)}
      >
        <div className="assessmentInfo">
          <div className="assessmentInfo__hero">
            <Space size={12} align="start">
              <div className="assessmentInfo__iconWrap">
                <InfoCircleOutlined />
              </div>
              <Space direction="vertical" size={0}>
                <Typography.Title level={3}>Before you begin</Typography.Title>
                <Typography.Text>
                  Review the assessment rules and format for <strong>{lessonQuery?.data?.current?.title}</strong>.
                </Typography.Text>
              </Space>
            </Space>
          </div>

          <Row gutter={[12, 12]}>
            <Col xs={24} sm={8}>
              <Card bordered={false}>
                <Statistic
                  title={
                    <Space size={6}>
                      <UnorderedListOutlined />
                      Questions
                    </Space>
                  }
                  value={assessmentInfo.questionCount}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card bordered={false}>
                <Statistic
                  title={
                    <Space size={6}>
                      <ReloadOutlined />
                      Attempts
                    </Space>
                  }
                  value={assessmentInfo.attempts}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card bordered={false}>
                <Statistic
                  title={
                    <Space size={6}>
                      <ClockCircleOutlined />
                      Time Limit
                    </Space>
                  }
                  value={assessmentInfo.timeLimitMinutes || "None"}
                  suffix={assessmentInfo.timeLimitMinutes ? "min" : ""}
                />
              </Card>
            </Col>
          </Row>

          <Alert
            className="assessmentInfo__alert"
            type={assessmentInfo.passScoreRequired ? "warning" : "info"}
            showIcon
            message={
              assessmentInfo.passScoreRequired
                ? `Passing requires at least ${assessmentInfo.passScore}%`
                : "Passing score is not required to continue"
            }
            description={
              assessmentInfo.passScoreRequired
                ? "You need to meet the minimum score to pass this assessment."
                : assessmentInfo.passScore
                ? `A pass score (${assessmentInfo.passScore}%) is set for reporting, but progression is not blocked by score.`
                : "You can continue learning even if you do not pass this assessment."
            }
          />

          <Descriptions column={1} size="small" bordered className="assessmentInfo__rules">
            <Descriptions.Item
              label={
                <Space size={6}>
                  <ReloadOutlined />
                  Repeat failed questions
                </Space>
              }
            >
              <Tag color={assessmentInfo.repeatFailedQuestions ? "green" : "default"}>
                {assessmentInfo.repeatFailedQuestions ? "Enabled" : "Disabled"}
              </Tag>
              {assessmentInfo.repeatFailedQuestions
                ? " If you retry after failing, only failed questions are repeated."
                : " You will retake all questions on another attempt."}
            </Descriptions.Item>
            <Descriptions.Item
              label={
                <Space size={6}>
                  <OrderedListOutlined />
                  Complete in order
                </Space>
              }
            >
              <Tag color={assessmentInfo.completeQuestionsInOrder ? "gold" : "default"}>
                {assessmentInfo.completeQuestionsInOrder ? "Required" : "Not required"}
              </Tag>
              {assessmentInfo.completeQuestionsInOrder
                ? " Answer questions from first to last."
                : " You may answer questions in any order."}
            </Descriptions.Item>
            <Descriptions.Item
              label={
                <Space size={6}>
                  <EyeOutlined />
                  Result review
                </Space>
              }
            >
              <Tag color={assessmentInfo.revealResult ? "blue" : "default"}>
                {assessmentInfo.revealResult ? "Available" : "Not available"}
              </Tag>
              {assessmentInfo.revealResult
                ? " You can review correct and incorrect answers after passing."
                : " Results are not shown in review mode after completion."}
            </Descriptions.Item>
            <Descriptions.Item
              label={
                <Space size={6}>
                  <TrophyOutlined />
                  Pass score
                </Space>
              }
            >
              {assessmentInfo.passScoreRequired
                ? `Minimum required: ${assessmentInfo.passScore}%`
                : assessmentInfo.passScore
                ? `Configured at ${assessmentInfo.passScore}% (not mandatory)`
                : "No configured minimum score"}
            </Descriptions.Item>
          </Descriptions>
        </div>
      </Modal>
      <header className="leasson__header" style={{ background: "#F9F9F9" }}>
        <div className="content">
          <Space size={0} direction="vertical" className="assessmentHeader__meta">
            <p className="badge--meta"> {ctxState?.learning?.kind}</p>
            <Space direction="vertical" size={4} className="assessmentHeader__titleBlock">
              <Typography.Title style={{ margin: 0 }} level={3}>
                {lessonQuery?.data?.current?.title}
              </Typography.Title>
              <Space size={8}>
                <p style={{ margin: 0 }} className="assessmentHeader__attempt">
                  Attempt: {assessmentQuery?.data?.currentAttempt}/{assessmentQuery?.data?.allowedAttempts}
                </p>
              </Space>
            </Space>
          </Space>
          <Space className="assessmentHeader__actions">
            {assessmentQuery?.data?.timeLimit && !assessmentAttemptModal && !assessmentQuery?.data?.isPreviewing && (
              <Countdown
                value={
                  new Date(
                    new Date(assessmentQuery?.data?.startedAt).getTime() + assessmentQuery?.data?.timeLimit * 60 * 1000
                  )
                }
                onFinish={onCountdownFinished}
              />
            )}
            <Space>
              <Button
                type="text"
                size="small"
                icon={<InfoCircleOutlined />}
                onClick={() => setAssessmentInfoModalVisible(true)}
              >
                Assessment info
              </Button>
              <Link to="/">
                <Button type="link">{t("endLearning")}</Button>
              </Link>
            </Space>
          </Space>
        </div>
      </header>
      <div
        id="my-scroll-layout"
        className={`answer__panel ${assessmentQuery?.data?.isPreviewing ? "answer__panel--review" : ""}`}
      >
        <Form
          // preserve
          form={form}
          // initialValues={state.preview && state.previwSubmission ? state.previwSubmission : state.initialAnswers}
          // initialValues={assessmentQuery?.data?.isPreviewing ? initialValues : {}}
          initialValues={initialValues}
        >
          <fieldset
            disabled={assessmentQuery?.data?.isPreviewing}
            style={{ paddingBottom: assessmentQuery?.data?.isPreviewing ? "7rem" : 0 }}
          >
            {assessmentQuery?.data?.questions
              // ?.slice(0, assessmentQuery?.data?.isPreviewing ? assessmentQuery?.data?.questions?.length : state.idx + 1)
              .map((question, qi) => (
                <React.Fragment key={question.uid}>
                  <div
                    id={question.uid}
                    className={`question ${assessmentQuery?.data?.isPreviewing ? "question--review" : ""}`}
                  >
                    {question?.imageUrl && (
                      <img src={question?.imageUrl} alt="assessment img" className="question__image" />
                    )}
                    {question?.videoUrl && (
                      <video controls src={question?.videoUrl} alt="assessment video" className="question__video" />
                    )}
                    <div className="assessment__header">
                      <Space size={0}>
                        <p className="badge--meta badge--meta--noSpace">
                          {/* questionNumberString only exists if it is a repeated question. It gets set in the initializer */}
                          {t("Question")} {question?.questionNumberString ? question?.questionNumberString : qi + 1}{" "}
                          {t("of")} {assessmentQuery?.data?.questions?.length}:{" "}
                        </p>
                        <Tag color="green">
                          {(() => {
                            switch (question.type) {
                              case "ture-false":
                                return t("trueFalse")
                              case "multiple-choice":
                                return t("multipleChoice")
                              case "scale-rating":
                                return t("scaleRating")
                              case "free-text":
                                return t("freeText")
                              case "order-correctly":
                                return t("orderCorrectly")
                              case "match-columns":
                                return t("matchColumns")
                              case "fill-in-missing":
                                return t("fillInMissing")

                              default:
                                return "Question"
                            }
                          })()}
                        </Tag>
                      </Space>
                      {(() => {
                        let questionText =
                          question?.type === "true-false" ||
                          question?.type === "multiple-choice" ||
                          question?.type === "scale-rating" ||
                          question?.type === "free-text" ||
                          question?.type === "order-correctly"
                            ? question?.content?.title
                            : question?.content?.summary

                        if (question?.type === "scale-rating" && questionText) {
                          questionText = questionText.replaceAll("[$]", "")
                        }

                        if (containsHTML(questionText)) {
                          // Clean up the HTML string by removing \n characters and wrapping quotes
                          const cleanHtml = questionText.replace(/\\n/g, "").replace(/^"|"$/g, "")
                          return <div dangerouslySetInnerHTML={{ __html: cleanHtml }} />
                        } else {
                          return (
                            <Typography.Title style={{ margin: 0, fontWeight: "normal" }} level={3}>
                              <BoldText text={questionText} />
                            </Typography.Title>
                          )
                        }
                      })()}
                    </div>
                    <Divider />
                    {(() => {
                      switch (question.type) {
                        case "multiple-choice":
                          return (
                            <MultipleChoice
                              trackQuestionSubmission={trackQuestionSubmission}
                              question={question}
                              questionIndex={qi}
                              form={form}
                              disabled={assessmentQuery?.data?.isPreviewing}
                              inReview={assessmentQuery?.data?.isPreviewing}
                              isFacilitate={isFacilitate}
                              required={true}
                              // required={assessment?.questionsRequirePreviousAnswer}
                            />
                          )
                        case "scale-rating":
                          return (
                            <ScaleRating
                              trackQuestionSubmission={trackQuestionSubmission}
                              question={question}
                              questionIndex={qi}
                              form={form}
                              disabled={assessmentQuery?.data?.isPreviewing}
                              inReview={assessmentQuery?.data?.isPreviewing}
                              isFacilitate={isFacilitate}
                              required={true}
                              // required={assessment?.questionsRequirePreviousAnswer}
                            />
                          )
                        case "free-text":
                          return (
                            <FreeText
                              trackQuestionSubmission={trackQuestionSubmission}
                              question={question}
                              form={form}
                              disabled={assessmentQuery?.data?.isPreviewing}
                              inReview={assessmentQuery?.data?.isPreviewing}
                              required={true}
                            />
                          )
                        case "true-false":
                          return (
                            <TrueAndFalse
                              trackQuestionSubmission={trackQuestionSubmission}
                              question={question}
                              questionIndex={qi}
                              disabled={assessmentQuery?.data?.isPreviewing}
                              inReview={assessmentQuery?.data?.isPreviewing}
                              isFacilitate={isFacilitate}
                              required={true}
                              // required={assessment?.questionsRequirePreviousAnswer}
                            />
                          )
                        case "order-correctly":
                          return (
                            <OrderCorrectly
                              trackQuestionSubmission={trackQuestionSubmission}
                              question={question}
                              questionIndex={qi}
                              form={form}
                              disabled={assessmentQuery?.data?.isPreviewing}
                              inReview={assessmentQuery?.data?.isPreviewing}
                              isFacilitate={isFacilitate}
                              required={true}
                              // required={assessment?.questionsRequirePreviousAnswer}
                            />
                          )
                        case "match-columns":
                          return (
                            <MatchColumns
                              trackQuestionSubmission={trackQuestionSubmission}
                              question={question}
                              questionIndex={qi}
                              form={form}
                              disabled={assessmentQuery?.data?.isPreviewing}
                              inReview={assessmentQuery?.data?.isPreviewing}
                              isFacilitate={isFacilitate}
                              required={true}
                              // required={assessment?.questionsRequirePreviousAnswer}
                            />
                          )
                        // case "fill-in-missing":
                        //   return (
                        //     <FillInMissing
                        //       assessment={assessmentQuery?.data}
                        //       question={question}
                        //       questionIndex={qi}
                        //       form={form}
                        //       disabled={assessmentQuery?.data?.isPreviewing}
                        //       inReview={assessmentQuery?.data?.isPreviewing}
                        //       isFacilitate={isFacilitate}
                        //       required={true}
                        //       // required={assessment?.questionsRequirePreviousAnswer}
                        //     />
                        //   )
                        default:
                          return <h2>Unsupported question type</h2>
                      }
                    })()}
                    {!assessmentQuery?.data?.isPreviewing && qi === assessmentQuery?.data?.questions?.length - 1 && (
                      <Footer style={{ background: "none" }} className="lesson__footer">
                        <Space size={16}>
                          <Card
                            disabled={ctxState.actionRunning}
                            onClick={async () => {
                              try {
                                setAssessmentAttemptModalFromSubmission(true)
                                const submission = await submitAssessment()
                                console.log("submission::: ", submission?.data)
                                if (submission?.data?.passed) {
                                  queryClient.invalidateQueries()
                                  window.location.reload()
                                } else {
                                  if (
                                    Number(submission?.data?.allowedAttempts ?? 0) +
                                      Number(submission?.data?.additionalAttempts || 0) >
                                    Number(submission?.data?.attemptsUsed ?? 0)
                                  ) {
                                    form.resetFields()
                                    setSavedSubmission(submission?.data)
                                    setAssessmentAttemptModal(true)
                                  } else {
                                    queryClient.invalidateQueries()
                                    window.location.reload()
                                  }
                                }
                              } catch (error) {
                                message.error(error.message)
                              }
                            }}
                            className="lesson__button lesson__button--next"
                            bordered
                            hoverable
                          >
                            <Space size={16}>
                              <Button
                                loading={ctxState?.actionRunning || isSubmitting}
                                type="primary"
                                shape="circle"
                                className="icon"
                                icon={<RightOutlined />}
                              />
                              <Space size={0} direction="vertical" align="start" className="button__content">
                                <Typography.Text className="title" strong>
                                  Submit Assessment
                                </Typography.Text>
                                <Typography.Text className="description" style={{ color: "white" }}>
                                  {t("andContinueLearning")}
                                </Typography.Text>
                              </Space>
                            </Space>
                          </Card>
                        </Space>
                      </Footer>
                    )}
                  </div>
                  {assessmentQuery?.data?.isPreviewing && qi < assessmentQuery?.data?.questions?.length - 1 && (
                    <Divider />
                  )}
                </React.Fragment>
              ))}
          </fieldset>
        </Form>
        {assessmentQuery?.data?.isPreviewing && (
          <Footer style={{ background: "none" }} className="lesson__footer">
            <Space size={16}>
              <Card
                disabled={ctxState.actionRunning}
                onClick={async () => {
                  ctxDispatch({
                    type: "next",
                    payload: {
                      nextUrlByPathname,
                      queryClient,
                      lessonUid,
                      enrolmentUid,
                      enrolmentType,
                      currentModule: moduleQuery?.data,
                      ...lessonQuery?.data?.next,
                    },
                  })
                }}
                className="lesson__button lesson__button--next"
                bordered
                hoverable
              >
                <Space size={16}>
                  <Button
                    loading={ctxState?.actionRunning}
                    type="primary"
                    shape="circle"
                    className="icon"
                    icon={<RightOutlined />}
                  />
                  <Space size={0} direction="vertical" align="start" className="button__content">
                    <Typography.Text className="title" strong>
                      {t("finishReview")}
                    </Typography.Text>
                    <Typography.Text className="description" style={{ color: "white" }}>
                      {t("andContinueLearning")}
                    </Typography.Text>
                  </Space>
                </Space>
              </Card>
            </Space>
          </Footer>
        )}
      </div>
    </Layout>
  )
}
